import { useSnackbar } from '@brands/hooks/useStackbar';
import { useAppDispatch } from '@brands/hooks/useReduxHook';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { OptionLanguage } from '@brands/Utils/selectOptions';
import React, { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import Modal, { Styles } from 'react-modal';

import EditDependent from '../../../MyProfile/Patient/Sections/Dependent/EditDependent';
import { UserProfile } from '../../../../services/identity/types/UserProfile';
import styles from '../../../PatientPlan/components/AddDependentModal/styles.module.scss';

interface CustomStylesDashboard {
  overlay: Styles['overlay'];
  content: Styles['content'];
}

const modalStyles: CustomStylesDashboard = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
    backgroundColor: '#1f2b38f0',
    overflowY: 'hidden',
  },
  content: {
    ...customStylesDashboard.content,
    width: '100%',
    maxWidth: '90vw',
    height: '90%',
    maxHeight: '90vh',
    backgroundColor: '#F0F0F0',
    borderRadius: '11px',
    opacity: '1',
    padding: '0',
    alignItems: 'flex-start',
  },
};

export type CollectorAddDependentModalProps = {
  open: boolean;
  onClose: () => void;
  guardianUser: UserProfile;
  /** `0` = add new dependent; otherwise edit existing dependent user id */
  dependentId: number;
  onDependentSaved: (dependentId: number, isNew: boolean) => void | Promise<void>;
};

const CollectorAddDependentModal: FC<CollectorAddDependentModalProps> = ({
  open,
  onClose,
  guardianUser,
  dependentId,
  onDependentSaved,
}) => {
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbar();
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [validationResponseId, setValidationResponseId] = useState('');
  const [showDeleteDependentConfirmationModal, setShowDeleteDependentConfirmationModal] = useState(false);
  const [reduxReady, setReduxReady] = useState(false);

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    setLanguageOptions(
      data.map((language) => ({
        value: language.locale,
        label: language.name,
        id: language.id,
      })),
    );
  };

  useLayoutEffect(() => {
    if (open && guardianUser) {
      dispatch(setSelectedPatientByAdmin({ selectedPatient: guardianUser }));
      setReduxReady(true);
    } else {
      setReduxReady(false);
    }
  }, [open, guardianUser, dispatch]);

  useEffect(() => {
    if (open) {
      void fetchLanguageOptions();
    }
  }, [open]);

  const setEditDependentOpen = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (value) => {
      const next = typeof value === 'function' ? value(open) : value;
      if (!next) {
        onClose();
      }
    },
    [open, onClose],
  );

  const isAddMode = !dependentId;
  const modalLabel = isAddMode ? 'Add dependent' : 'Edit dependent';

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel={modalLabel}
      ariaHideApp={false}
    >
      <div className={styles.profileContainer}>
        <div className={styles.contentContainer} style={{ padding: '40px 80px' }}>
          {open && reduxReady && (
            <EditDependent
              key={`collector-dependent-${dependentId}-${open}`}
              setIsEditDependent={setEditDependentOpen}
              dependentId={dependentId}
              languageOptions={languageOptions}
              isModal
              validationResponseId={validationResponseId}
              setValidationResponseId={setValidationResponseId}
              isFromSelectNeeds={false}
              showDeleteDependentConfirmationModal={showDeleteDependentConfirmationModal}
              setShowDeleteDependentConfirmationModal={setShowDeleteDependentConfirmationModal}
              showSnackbar={showSnackbar}
              isFromPatientIntake={false}
              isFromCollectorAddDependent
              onCollectorDependentCreated={onDependentSaved}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

Modal.setAppElement('#root');

export default CollectorAddDependentModal;
